import React, { useEffect, useRef, useState } from 'react';
import { AvGroup, AvInput } from 'availity-reactstrap-validation';
import { FormattedMessage, useIntl } from 'react-intl';
import Select, { createFilter } from 'react-select';
import { Label } from 'reactstrap';
import classnames from 'classnames';
import { getLocaleHierarchy } from '../../../../utility/util/util.service';
import Creatable from 'react-select/creatable';

/**
 * DropdownField component is a custom React-select input field compatible with forms.
 * It manages a hidden input field to store the selected value(s) since React Select is not directly compatible with forms.
 *
 * @param {string} [type] - The type of the resulting (hidden) input field. Defaults to text. See: https://www.w3schools.com/html/html_form_input_types.asp
 * @param {string} [className] - Additional class names for the React Select container.
 * @param {string} [groupClassName] - Additional class names for AvGroup
 * @param {Array} [values] - Preselected values that should match the format returned by getOptionValue. Can also be a single value!
 * @param {undefined | value} [emptyValue] - The value that will be returned, if there are no selected options. Defaults to undefined.
 * @param {string} [propertyName] - The name of the property to extract from the object.
 * @param {Object} [object] - The object containing the property to be used for preselecting values.
 * @param {Array} options - The options to display in the dropdown.
 * @param {function} [getOptionLabel] - Function to extract the label from an option (default: returns option.label or option.value).
 * @param {function} [getOptionValue] - Function to extract the value from an option (default: returns option.value or the option itself).
 * @param {boolean} [isMulti=false] - Indicates if multiple selections are allowed.
 * @param {function} [onChange] - Callback function triggered when the selection changes.
 * @param {function} [onFocus] - Callback function triggered when the dropdown is focused.
 * @param {string} [placeholderMessageId] - ID for the placeholder message to be localized using react-intl.
 * @param {string} [labelMessageId] - ID for the label message to be localized using react-intl.
 * @param {boolean} [disabled] - If truthy, all options will be disabled
 * @param {boolean} [required=false] - Indicates if the field is required.
 * @param {Object} [props] - Additional properties to pass to the React Select component.
 *
 *
 * @example
 * <DropdownField
 *   type="text"
 *   className="my-dropdown"
 *   groupClassName="my-dropdown"
 *   values={["value1"]}
 *   propertyName="property"
 *   object={{ property: "value1" }}
 *   options={[{ value: "value1", label: "Label 1" }, { value: "value2", label: "Label 2" }]}
 *   getOptionLabel={(option) => option.label}
 *   getOptionValue={(option) => option.value}
 *   isMulti={false}
 *   onChange={(selectedOptions, values) => console.log(selectedOptions, values)}
 *   onFocus={() => console.log('Focused')}
 *   placeholderMessageId="dropdown.placeholder"
 *   labelMessageId="dropdown.label"
 *   required
 * />
 */

// as React Select is not compatible with forms, we need to add a hidden field and inject the selected value(s)
// if isMulti=true, you will need to JSON.parse the value, because it will be a stringified array. Else, it's just a string.
const DropdownField = ({
  type,
  className,
  groupClassName,
  values,
  emptyValue,
  propertyName,
  object,
  options,
  getOptionLabel = (option) => option?.label || option?.value || option,
  getOptionValue = (option) => option?.value || option,
  isMulti,
  onChange,
  onFocus,
  placeholderMessageId,
  labelMessageId,
  disabled,
  required,
  inputRef,
  isCreateable,
  isKiosk,
  ...props
}) => {
  const intl = useIntl();
  const [selectedOptions, setSelectedOptions] = useState(null);
  const reactSelectRef = useRef();
  const inputFieldRef = useRef();
  const inputFieldFormCtrl =
    inputRef?.current?.context?.FormCtrl ||
    inputFieldRef?.current?.context?.FormCtrl;

  // concats all nameXX properties, to be able to search for a name in a different langugage
  // eg. you search for "Salz", it will return Salt, as Salz is german for Salt
  // result search string will be <nameDE>;<nameEN>;...
  const stringifyLocalized = (option) =>
    option?.type === 'FoodIngredient'
      ? getLocaleHierarchy(intl.locale).reduce(
          (searchString, locale) =>
            searchString + (option.data['name' + locale] || '') + ';',
          '',
        )
      : option?.label;

  // extracts the value(s) from given options depending on isMulti (=> either array of values or single value. value is normally string or number.)
  const getValues = (options, toJson = false) => {
    if (!options?.length) return emptyValue;
    const value = isMulti
      ? options.map(getOptionValue) // get the values as array
      : getOptionValue(options[0]); // get the value (it's only one!)

    return toJson ? JSON.stringify(value) : value;
  };

  // pre-select options
  useEffect(() => {
    const rawValues = values || object?.[propertyName] || []; // values has the highest priority

    // from here on, selectedOption must always be an array (that's how React Select wants it)
    // when user passes values or object+propertyName, we need to pre-fill the selectedOptions
    const preparedValues = Array.isArray(rawValues) ? rawValues : [rawValues];

    // if isMulti=true, we expect an array of values
    if (isMulti && !Array.isArray(preparedValues)) {
      throw new Error(
        'When isMulti=true, you need to pass an Array of values!',
      );
    }

    // find the values in options array
    const optionsFound = preparedValues.map((value) =>
      options.find((option) => value === getOptionValue(option)),
    );

    setSelectedOptions(optionsFound);
  }, [values, object]);

  return (
    <AvGroup className={classnames('form-label-group', groupClassName)}>
      {!isCreateable ? (
        <Select
          ref={reactSelectRef}
          isMulti={isMulti}
          className={classnames('react-select-container', className)}
          classNamePrefix="react-select"
          menuPlacement={isKiosk ? 'top' : 'auto'}
          isOptionDisabled={() => disabled}
          menuShouldScrollIntoView={false} // Prevents unwanted scroll jumps
          captureMenuScroll={false} // Better mobile scroll handling
          closeMenuOnScroll={false}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({
              ...base,
              position: 'absolute', // Better for mobile
              zIndex: 9999,
              // Prevent mobile viewport zoom/scroll issues
              touchAction: 'none',
              WebkitOverflowScrolling: 'touch',
            }),
            control: (baseStyles) => ({
              ...baseStyles,
              borderColor:
                required &&
                inputFieldFormCtrl?.isTouched(propertyName) &&
                inputFieldFormCtrl?.hasError(propertyName)
                  ? '#dc2839 !important' // TODO: Change this to use is-invalid class after upgrade to https://react-select.com/styles#the-classnames-prop
                  : undefined,
            }),
          }}
          onFocus={(...args) => {
            // simulate that the (hidden) input field has been touched when actually the React Select field was touched
            if (inputFieldFormCtrl) inputFieldFormCtrl.setTouched(propertyName);
            if (onFocus) onFocus(...args);
          }}
          value={selectedOptions}
          options={options}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          onChange={(options) => {
            // if isMulti=true, options is an array
            // if isClearable=true is used, options will be null in the process of clearing
            const selectedOptions = options
              ? Array.isArray(options)
                ? options
                : [options]
              : []; // set empty array if falsy value was passed (e.g. when cleared)
            if (onChange) onChange(options, getValues(selectedOptions));
            setSelectedOptions(selectedOptions); // must always be an array
          }}
          placeholder={intl.formatMessage({
            id: placeholderMessageId || 'defaultDropdownPlaceholder',
          })}
          noOptionsMessage={() =>
            intl.formatMessage({ id: 'defaultDropdownNoOptions' })
          }
          filterOption={createFilter({ stringify: stringifyLocalized })}
          {...props}
        />
      ) : (
        <Creatable
          formatCreateLabel={(input) =>
            intl.formatMessage(
              { id: 'reactSelectCreateLabel' },
              { name: input },
            )
          }
          ref={reactSelectRef}
          isMulti={isMulti}
          className={classnames('react-select-container', className)}
          classNamePrefix="react-select"
          menuPortalTarget={document.body}
          menuPosition="fixed"
          isOptionDisabled={() => disabled}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
            control: (baseStyles) => ({
              ...baseStyles,
              borderColor:
                required &&
                inputFieldFormCtrl?.isTouched(propertyName) &&
                inputFieldFormCtrl?.hasError(propertyName)
                  ? '#dc2839 !important' // TODO: Change this to use is-invalid class after upgrade to https://react-select.com/styles#the-classnames-prop
                  : undefined,
            }),
          }}
          onFocus={(...args) => {
            // simulate that the (hidden) input field has been touched when actually the React Select field was touched
            if (inputFieldFormCtrl) inputFieldFormCtrl.setTouched(propertyName);
            if (onFocus) onFocus(...args);
          }}
          value={selectedOptions}
          options={options}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          onChange={(options) => {
            // if isMulti=true, options is an array
            // if isClearable=true is used, options will be null in the process of clearing
            const selectedOptions = options
              ? Array.isArray(options)
                ? options
                : [options]
              : []; // set empty array if falsy value was passed (e.g. when cleared)
            if (onChange) onChange(options, getValues(selectedOptions));
            setSelectedOptions(selectedOptions); // must always be an array
          }}
          placeholder={intl.formatMessage({
            id: placeholderMessageId || 'defaultDropdownPlaceholder',
          })}
          noOptionsMessage={() =>
            intl.formatMessage({ id: 'defaultDropdownNoOptions' })
          }
          filterOption={createFilter({ stringify: stringifyLocalized })}
          {...props}
        />
      )}
      {labelMessageId && (
        <Label className="floating-label" color="primary">
          <FormattedMessage id={labelMessageId} />
        </Label>
      )}
      <AvInput
        ref={inputRef || inputFieldRef}
        type={(!isMulti && type) || 'text'} // if multi, type must be string, because we use JSON.stringify
        name={propertyName}
        value={getValues(selectedOptions, isMulti)}
        // we need the parser to parse JSON back to an array
        valueParser={(value) => {
          if (value || value === emptyValue) {
            if (!isMulti) return value;

            // is isMulti is not constant (e.g. comparing to a value from redux), there is a short time, where it becomes falsy.
            // so try to parse the value, but if not, just return the value
            try {
              return JSON.parse(value);
            } catch (e) {
              return value; // if parsing fails, just return the value
            }
          }
        }}
        required={required}
        hidden
      />
    </AvGroup>
  );
};

export default DropdownField;
